import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import BugReportIcon from '@mui/icons-material/BugReport';

import { PredictionPosition, PositionsGroupedByPair } from '../../types';
import {
  UnwindingRule,
  UpdateUnwindingRuleDto
} from '../../types/unwinding.types';
import { useUnwindingSocket } from '../../hooks/useUnwindingSocket';
import {
  useSetPairUnwinderEnabled,
  useBulkSetPairUnwinderEnabled
} from '../../api/useUnwinding';
import { BulkActionBar } from '../BulkActionBar';
// Note: the FE no longer computes any sim values locally. SELL-direction
// `engineSim` + `simCells` come straight from the Rust unwinder engine,
// forwarded through the Phase-6 envelope. Backend is the single source
// of truth — `—` is the only fallback the UI ever renders.
import type {
  EnrichedRuleStatus,
  UnwindingEnginePathSim,
  UnwindingSimCell,
} from '../../types/unwinding.types';

/**
 * Pick the path-A or path-B `PathSim` from the backend's
 * `engineSim` based on which platform-and-side the position is on.
 *
 *   Path A = `poly_yes + kalshi_no`.
 *   Path B = `poly_no  + kalshi_yes`.
 *
 * The unwinder UI's "Live Sums" + "Sim" columns mirror the position's
 * own side, so we pick A when the Polymarket leg is on YES, else B.
 * Returns `null` when the backend has no sim yet — the row renders `—`
 * (no FE-local fallback, ever).
 */
function pickEnginePathSim(
  liveState: EnrichedRuleStatus | undefined,
  polySide: 'yes' | 'no',
): UnwindingEnginePathSim | null {
  const sim = liveState?.engineSim ?? null;
  if (!sim) return null;
  return polySide === 'no' ? sim.b : sim.a;
}

/**
 * Pick the SELL-direction maker-sim cell for the position's side.
 * The 4-cell `SimCells` is direction-agnostic in shape — the FE picks
 * the cell whose maker leg matches the position's side per platform.
 */
function pickPolyMakerCell(
  liveState: EnrichedRuleStatus | undefined,
  polySide: 'yes' | 'no',
): UnwindingSimCell | null {
  const cells = liveState?.simCells ?? null;
  if (!cells) return null;
  return polySide === 'no' ? cells.polyNoMaker : cells.polyYesMaker;
}

function pickKalshiMakerCell(
  liveState: EnrichedRuleStatus | undefined,
  kalshiSide: 'yes' | 'no',
): UnwindingSimCell | null {
  const cells = liveState?.simCells ?? null;
  if (!cells) return null;
  return kalshiSide === 'no' ? cells.kalshiNoMaker : cells.kalshiYesMaker;
}

const fmtPrice = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v === 0) return '0.000';
  if (v >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};

const fmtQty = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v <= 0) return '—';
  if (v >= 1_000_000) return `${Math.floor(v / 100_000) / 10}M`;
  if (v >= 1_000) return `${Math.floor(v / 100) / 10}k`;
  if (v >= 100) return String(Math.floor(v));
  return v.toFixed(2);
};

const fmtContracts = (v: number | undefined | null): string => {
  if (v == null || v === 0) return '—';
  return Math.round(v).toString();
};

// Display-side mirror of `RateCalculator.calculatePositionTna` (continuous
// compounding) for the taker-taker scenario: sum = both legs' net taker
// bids, profit = (1/sum) − 1, TNA = ln(1+profit) / (days/365) × 100.
// Hour-floor matches the backend (`MIN_DAYS = 1/24`); fully-expired
// markets get a null result so the cell renders a dash.
const MIN_DAYS = 1 / 24;
const fmtPct = (v: number | null): string => {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
};
const takerTnaFromSum = (
  sum: number | null,
  daysToExpiry: number | null | undefined
): number | null => {
  if (sum == null || sum <= 0 || daysToExpiry == null) return null;
  if (daysToExpiry < -1) return null;
  const days = Math.max(MIN_DAYS, daysToExpiry);
  const profit = 1 / sum - 1;
  return (Math.log(1 + profit) / (days / 365)) * 100;
};

// Pick the dominant open position for a platform — the one with the largest
// contract count. The upstream sometimes returns multiple open documents per
// platform after a side flip (closed YES, opened NO, etc.) before the stale
// leg is reconciled. Picking the *first* match leaves the choice up to array
// order and can disagree with what the position-unwinder service resolves to,
// which then displays YES chip / NO-side prices on the row. Mirrors
// PositionsSyncService.extractOpenPosition on the backend.
const findPos = (
  group: PositionsGroupedByPair,
  platform: 'polymarket' | 'kalshi'
): PredictionPosition | undefined => {
  let best: PredictionPosition | undefined;
  let bestContracts = 0;
  for (const p of group.positions) {
    if (p.platform !== platform || p.status !== 'open') continue;
    const contracts = Number(p.contracts) || 0;
    if (contracts <= 0) continue;
    if (contracts > bestContracts) {
      best = p;
      bestContracts = contracts;
    }
  }
  return best;
};

const hSx = {
  fontSize: '0.58rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.2,
  px: 0.1,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};
const gSx = {
  ...hSx,
  textAlign: 'center' as const,
  borderBottom: '1px solid rgba(255,255,255,0.1)'
};
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.05 } as const;

export interface CreateSumRuleParams {
  group: PositionsGroupedByPair;
  platform: 'kalshi' | 'polymarket';
  thresholdSum: number;
  size: number;
  maxVolumeContracts: number;
}

interface Props {
  groups: PositionsGroupedByPair[];
  /** Rules of the `sum_of_sides` type — drives this tab's row controls
   *  (sim params, platform toggle, create dialog). */
  rules: UnwindingRule[] | undefined;
  /** All rules (any type / status) keyed by pairId — used only by the
   *  dev-logs button so a row stays clickable even when the pair's rule
   *  is on the other tab type. */
  anyRulesByPairId: Map<string, UnwindingRule>;
  getState: ReturnType<typeof useUnwindingSocket>['getState'];
  onCreateRule: (params: CreateSumRuleParams) => Promise<void>;
  onTogglePlatform: (
    rule: UnwindingRule,
    platform: 'kalshi' | 'polymarket',
    enabled: boolean
  ) => Promise<void>;
  onUpdateRule: (rule: UnwindingRule, dto: UpdateUnwindingRuleDto) => Promise<boolean>;
  onOpenDebug: (ruleId: string, pairName: string) => void;
}

const DEFAULT_THRESHOLD = 1.0;
const DEFAULT_SIZE = 50;
const DEFAULT_MAX_VOL = 500;

// Proportional column widths fed to a <colgroup>. The Table uses
// `tableLayout: 'fixed'` so the colgroup % is authoritative; without
// this, the 25 per-cell pixel widths sum to ~1024 px and the browser
// renders the table at that natural size instead of stretching to fill
// a wider container. Order matches the TableHead row 1 + row 2 column
// sequence (checkbox, Market, Pos, then market-data / parameters /
// live-sums / simulation / ctrl groups left-to-right).
const COL_WIDTHS_PX = [
  18, 310, 30,
  20, 28, 28, 20, 20, 28, 28, 20,
  40, 30, 36,
  38, 38, 40, 40, 44,
  28, 28, 28, 28,
  28, 28,
] as const;
const COL_TOTAL_PX = COL_WIDTHS_PX.reduce((s, w) => s + w, 0);

// `tna` asc is the default — surface the pairs closest to (or past) the
// "exit now" line first. Operators can click any column header to switch.
type SortKey = 'pos' | 'mtPoly' | 'mtKalshi' | 'tt' | 'delta' | 'tna';
type SortDir = 'asc' | 'desc';

// Largest-contracts open position per platform — matches `findPos` above.
const getMaxPositionSize = (group: PositionsGroupedByPair): number => {
  const poly = findPos(group, 'polymarket');
  const kalshi = findPos(group, 'kalshi');
  return Math.max(poly?.contracts ?? 0, kalshi?.contracts ?? 0);
};

export const UnwindingTabSumOfSides = ({
  groups,
  rules,
  anyRulesByPairId,
  getState,
  onCreateRule,
  onTogglePlatform,
  onUpdateRule,
  onOpenDebug
}: Props) => {
  const [sortKey, setSortKey] = useState<SortKey>('tna');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const rulesByPairId = useMemo(() => {
    const map = new Map<string, UnwindingRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      map.set(rule.pairId, rule);
    }
    return map;
  }, [rules]);

  const handleSortClick = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortKey(key);
        setSortDir('desc');
      }
    },
    [sortKey]
  );

  // Build sort metrics per group from the same data the rows use, so the
  // displayed values and the sort key always agree. Rules are looked up by
  // pairId and live bids fall back to position top-of-book when WS is silent.
  //
  // Two-tier sort: pairs with NO live sim info (no orderbook prices on
  // either leg) always sink to the bottom, regardless of which column or
  // direction is active. Within each tier, the active metric drives the
  // order. So if you sort by Position desc, you see "biggest positions
  // with live data" first, "biggest positions without live data" at the
  // very end — instead of a row with phantom 0s mixed into the top.
  const sortedGroups = useMemo(() => {
    const annotated = groups.map(group => {
      const rule = group._id ? rulesByPairId.get(group._id) : undefined;
      const liveState =
        (group._id ? getState(group._id) : undefined) ??
        (rule ? getState(rule._id) : undefined);
      // SELL-direction sim comes straight from the Rust unwinder engine.
      // Sort metrics MUST agree with the row body — both consult the
      // same `liveState.engineSim` payload. No FE-local fee math here.
      const polySide = findPos(group, 'polymarket')?.side ?? 'yes';
      const pathSim = pickEnginePathSim(liveState, polySide);
      const mtPoly = pathSim?.sumPolyMk ?? null;
      const mtKalshi = pathSim?.sumKalshiMk ?? null;
      const tt = pathSim?.sumTT ?? null;
      const threshold = rule?.thresholdSum ?? DEFAULT_THRESHOLD;
      const delta = tt != null ? tt - threshold : null;
      // Same continuous-compounding TNA the row body renders — keep
      // the sort metric and the displayed cell in lock-step.
      const tna = takerTnaFromSum(
        tt,
        liveState?.daysToExpiry ?? group.daysToExpiry ?? null
      );

      // "Has live sim info" gate: the row can compute at least one Live
      // Sums column (MT-P, MT-K, or TT). If all three are null, the row
      // shows only dashes in the simulation columns and contributes
      // nothing to the operator's decision — pin to the bottom.
      const hasSim = mtPoly != null || mtKalshi != null || tt != null;

      const metric =
        sortKey === 'pos'
          ? getMaxPositionSize(group)
          : sortKey === 'mtPoly'
          ? mtPoly
          : sortKey === 'mtKalshi'
          ? mtKalshi
          : sortKey === 'tt'
          ? tt
          : sortKey === 'tna'
          ? tna
          : delta;
      return { group, metric, hasSim };
    });
    annotated.sort((a, b) => {
      // Tier 1: no-sim rows always at the bottom, regardless of direction.
      if (a.hasSim !== b.hasSim) return a.hasSim ? -1 : 1;
      // Tier 2: within each tier, null metrics also sink (same direction-
      // independent rule the existing code applied).
      if (a.metric == null && b.metric == null) return 0;
      if (a.metric == null) return 1;
      if (b.metric == null) return -1;
      return sortDir === 'desc' ? b.metric - a.metric : a.metric - b.metric;
    });
    return annotated.map(x => x.group);
  }, [groups, rulesByPairId, getState, sortKey, sortDir]);

  /* ---------------------------------------------------------------- */
  /* Bulk market-data ON/OFF — multi-select + master toggle           */
  /* ---------------------------------------------------------------- */

  // Checked pair ids (keyed by group._id == pairId).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkUnwinder = useBulkSetPairUnwinderEnabled();

  const selectableIds = useMemo(
    () => sortedGroups.map(g => g._id).filter((id): id is string => !!id),
    [sortedGroups],
  );
  const allSelected =
    selectableIds.length > 0 && selected.size >= selectableIds.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelect = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected(prev =>
      prev.size >= selectableIds.length && selectableIds.length > 0
        ? new Set()
        : new Set(selectableIds),
    );
  }, [selectableIds]);

  // ─── Row virtualization (2026-05-23) ──────────────────────────────────
  // Mount only the visible `SumRow`s + a top/bottom spacer. SumRow has
  // no expanded-row variant (the toggle confirmation lives in a
  // separate Dialog), so a fixed `estimateSize` is enough — no
  // `measureElement` needed. See
  // ~/.claude/plans/smooth-twirling-lampson.md.
  const sumTableContainerRef = useRef<HTMLDivElement>(null);
  const sumRowVirtualizer = useVirtualizer({
    count: sortedGroups.length,
    getScrollElement: () => sumTableContainerRef.current,
    // ~54px per row — measured from a populated tab.
    estimateSize: () => 54,
    overscan: 6,
    getItemKey: index => {
      const group = sortedGroups[index] as any;
      return group?._id ?? group?.pairName ?? index;
    },
  });

  // scope:'ids' — flip the checked subset. Backend caps pairIds at 100
  // per request, so chunk larger selections.
  const applyBulkSelected = useCallback(
    async (enabled: boolean) => {
      const ids = [...selected];
      if (ids.length === 0) return;
      setBulkBusy(true);
      try {
        for (let i = 0; i < ids.length; i += 100) {
          await bulkUnwinder.mutateAsync({
            scope: 'ids',
            pairIds: ids.slice(i, i + 100),
            enabled,
          });
        }
        setSelected(new Set());
      } finally {
        setBulkBusy(false);
      }
    },
    [selected, bulkUnwinder],
  );

  // scope:'all' — flip every active non-archived pair server-side.
  const applyBulkAll = useCallback(
    async (enabled: boolean) => {
      setBulkBusy(true);
      try {
        await bulkUnwinder.mutateAsync({ scope: 'all', enabled });
        setSelected(new Set());
      } finally {
        setBulkBusy(false);
      }
    },
    [bulkUnwinder],
  );

  if (groups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
          No open positions found.
        </Typography>
      </Box>
    );
  }

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': { color: 'inherit' },
    '&.Mui-active': { color: '#42a5f5' },
    '& .MuiTableSortLabel-icon': { fontSize: '0.8rem' }
  } as const;

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <BulkActionBar
        totalCount={selectableIds.length}
        selectedCount={selected.size}
        busy={bulkBusy}
        disableWarning="Open positions are NOT closed — manage them manually after disabling."
        onEnableSelected={() => void applyBulkSelected(true)}
        onDisableSelected={() => void applyBulkSelected(false)}
        onClearSelection={() => setSelected(new Set())}
        onEnableAll={() => void applyBulkAll(true)}
        onDisableAll={() => void applyBulkAll(false)}
      />
      <TableContainer
        ref={sumTableContainerRef}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {COL_WIDTHS_PX.map((w, i) => (
            <col key={i} style={{ width: `${(w / COL_TOTAL_PX) * 100}%` }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ ...gSx, width: 18, px: 0, verticalAlign: 'middle', top: 0, backgroundColor: '#1a1a2e' }}
              rowSpan={2}
              align="center"
              padding="none"
            >
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleSelectAll}
                disabled={selectableIds.length === 0 || bulkBusy}
                sx={{ p: 0, color: 'grey.500', '& .MuiSvgIcon-root': { fontSize: 14 } }}
              />
            </TableCell>
            <TableCell sx={{ ...gSx, width: 310, top: 0, backgroundColor: '#1a1a2e' }} rowSpan={2}>
              Market
            </TableCell>
            <TableCell sx={{ ...gSx, width: 30, top: 0, backgroundColor: '#1a1a2e' }} rowSpan={2}>
              <TableSortLabel
                active={sortKey === 'pos'}
                direction={sortKey === 'pos' ? sortDir : 'desc'}
                onClick={() => handleSortClick('pos')}
                sx={sortLabelSx}
              >
                Pos
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={8}>
              Market Data
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={3}>
              Parameters
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={5}>
              Live Sums
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={4}>
              Simulation
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={2}>
              Ctrl
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ ...hSx, width: 20, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Bid</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Ask</TableCell>
            <TableCell sx={{ ...hSx, width: 20, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 20, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Bid</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Ask</TableCell>
            <TableCell sx={{ ...hSx, width: 20, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 40, top: 22, backgroundColor: '#1a1a2e' }} align="right">Thr</TableCell>
            <TableCell sx={{ ...hSx, width: 30, top: 22, backgroundColor: '#1a1a2e' }} align="right">Size</TableCell>
            <TableCell sx={{ ...hSx, width: 36, top: 22, backgroundColor: '#1a1a2e' }} align="right">MaxV</TableCell>
            <TableCell
              sx={{ ...hSx, width: 38, top: 22, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
              align="right"
              title="Polymarket-as-maker direction: P.Ask + K.Bid. Click to sort."
              onClick={() => handleSortClick('mtPoly')}
            >
              <TableSortLabel
                active={sortKey === 'mtPoly'}
                direction={sortKey === 'mtPoly' ? sortDir : 'desc'}
                sx={sortLabelSx}
              >
                MT-P
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 38, top: 22, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
              align="right"
              title="Kalshi-as-maker direction: K.Ask + P.Bid. Click to sort."
              onClick={() => handleSortClick('mtKalshi')}
            >
              <TableSortLabel
                active={sortKey === 'mtKalshi'}
                direction={sortKey === 'mtKalshi' ? sortDir : 'desc'}
                sx={sortLabelSx}
              >
                MT-K
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 40, top: 22, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
              align="right"
              title="Taker-taker (both legs cross at top bid): P.Bid + K.Bid. Headline live sum. Click to sort."
              onClick={() => handleSortClick('tt')}
            >
              <TableSortLabel
                active={sortKey === 'tt'}
                direction={sortKey === 'tt' ? sortDir : 'desc'}
                sx={sortLabelSx}
              >
                TT
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 40, top: 22, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
              align="right"
              title="TT − Threshold (>= 0 means the rule can fire right now). Click to sort."
              onClick={() => handleSortClick('delta')}
            >
              <TableSortLabel
                active={sortKey === 'delta'}
                direction={sortKey === 'delta' ? sortDir : 'desc'}
                sx={sortLabelSx}
              >
                Δ
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 44, top: 22, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
              align="right"
              title="Annualized return if BOTH legs execute as taker (TT-based, continuous compounding). Click to sort."
              onClick={() => handleSortClick('tna')}
            >
              <TableSortLabel
                active={sortKey === 'tna'}
                direction={sortKey === 'tna' ? sortDir : 'desc'}
                sx={sortLabelSx}
              >
                TNA
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }}
              align="right"
              title="Polymarket-as-maker: counter (Kalshi) bid we'd take"
            >
              K.Bid
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }}
              align="right"
              title="Polymarket-as-maker: maker rest price = Threshold − K.Bid"
            >
              P.Ask
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }}
              align="right"
              title="Kalshi-as-maker: counter (Polymarket) bid we'd take"
            >
              P.Bid
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }}
              align="right"
              title="Kalshi-as-maker: maker rest price = Threshold − P.Bid"
            >
              K.Ask
            </TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center">P</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center">K</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(() => {
            const virtualItems = sumRowVirtualizer.getVirtualItems();
            const totalSize = sumRowVirtualizer.getTotalSize();
            const paddingTop =
              virtualItems.length > 0 ? virtualItems[0].start : 0;
            const paddingBottom =
              virtualItems.length > 0
                ? totalSize - virtualItems[virtualItems.length - 1].end
                : 0;
            return (
              <>
                {paddingTop > 0 && (
                  <TableRow style={{ height: paddingTop }}>
                    <TableCell
                      colSpan={100}
                      sx={{ p: 0, border: 'none' }}
                      aria-hidden
                    />
                  </TableRow>
                )}
                {virtualItems.map(vi => {
                  const group = sortedGroups[vi.index];
                  const rule = group._id
                    ? rulesByPairId.get(group._id)
                    : undefined;
                  // For the dev-logs button, fall back to ANY rule
                  // (incl. TNA / paused / completed) so a pair's logs
                  // stay reachable from this tab when its rule type
                  // doesn't match `sum_of_sides`.
                  const debugRuleId =
                    rule?._id
                    ?? (group._id
                      ? anyRulesByPairId.get(group._id)?._id
                      : undefined);
                  const liveState =
                    (group._id ? getState(group._id) : undefined)
                    ?? (rule ? getState(rule._id) : undefined);
                  return (
                    <SumRow
                      key={group._id ?? group.pairName}
                      group={group}
                      polyPos={findPos(group, 'polymarket')}
                      kalshiPos={findPos(group, 'kalshi')}
                      rule={rule}
                      debugRuleId={debugRuleId}
                      liveState={liveState}
                      selected={
                        group._id ? selected.has(group._id) : false
                      }
                      onToggleSelect={toggleSelect}
                      onCreateRule={onCreateRule}
                      onTogglePlatform={onTogglePlatform}
                      onUpdateRule={onUpdateRule}
                      onOpenDebug={onOpenDebug}
                    />
                  );
                })}
                {paddingBottom > 0 && (
                  <TableRow style={{ height: paddingBottom }}>
                    <TableCell
                      colSpan={100}
                      sx={{ p: 0, border: 'none' }}
                      aria-hidden
                    />
                  </TableRow>
                )}
              </>
            );
          })()}
        </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

interface RowProps {
  group: PositionsGroupedByPair;
  polyPos?: PredictionPosition;
  kalshiPos?: PredictionPosition;
  rule?: UnwindingRule;
  /** See `PositionRow.debugRuleId` in UnwindingPage — any-type any-status
   *  rule id for the dev-logs drawer trigger. Undefined when the pair has
   *  no rule of any kind. */
  debugRuleId?: string;
  liveState?: ReturnType<ReturnType<typeof useUnwindingSocket>['getState']>;
  /** Whether this row's bulk-select checkbox is ticked. */
  selected: boolean;
  /** Toggle this pair in the bulk-select set. */
  onToggleSelect: (id: string) => void;
  onCreateRule: (params: CreateSumRuleParams) => Promise<void>;
  onTogglePlatform: (
    rule: UnwindingRule,
    platform: 'kalshi' | 'polymarket',
    enabled: boolean
  ) => Promise<void>;
  onUpdateRule: (rule: UnwindingRule, dto: UpdateUnwindingRuleDto) => Promise<boolean>;
  onOpenDebug: (ruleId: string, pairName: string) => void;
}

const SumRow = memo(({
  group,
  polyPos,
  kalshiPos,
  rule,
  debugRuleId,
  liveState,
  selected,
  onToggleSelect,
  onCreateRule,
  onTogglePlatform,
  onUpdateRule,
  onOpenDebug
}: RowProps) => {
  const ruleSize =
    rule?.kalshiConfig?.contractsToSell ??
    rule?.polymarketConfig?.contractsToSell ??
    DEFAULT_SIZE;
  const initThreshold = rule?.thresholdSum != null ? String(rule.thresholdSum) : String(DEFAULT_THRESHOLD);
  const initSize = String(ruleSize);
  const initMaxVol = String(rule?.maxVolumeContracts ?? DEFAULT_MAX_VOL);

  const [threshold, setThreshold] = useState(initThreshold);
  const [size, setSize] = useState(initSize);
  const [maxVol, setMaxVol] = useState(initMaxVol);
  const [savedThreshold, setSavedThreshold] = useState(initThreshold);
  const [savedSize, setSavedSize] = useState(initSize);
  const [savedMaxVol, setSavedMaxVol] = useState(initMaxVol);
  const [saving, setSaving] = useState(false);
  const [togglingPoly, setTogglingPoly] = useState(false);
  const [togglingKalshi, setTogglingKalshi] = useState(false);

  // Whether the unwinder is actively MONITORING this pair — i.e. bringing
  // its market data. True only when there's a live state envelope AND the
  // per-pair kill switch is on. A pair the unwinder skips reads as NOT
  // monitored: either the kill switch is off, OR the pair never met the
  // subscription constraint (both legs open + above the contract minimum)
  // so it never gets a state envelope at all. The row's switch + every
  // market-data column key off this, so a non-monitored row honestly
  // shows OFF with empty ("—") market data instead of stale prices.
  const isMonitored = !!liveState && liveState.unwinderEnabled !== false;
  // Operator-disabled (has an envelope, kill switch off) vs
  // constraint-excluded (no envelope at all) — only differ in the tooltip.
  const killSwitchOff = !!liveState && liveState.unwinderEnabled === false;
  const hasOpenContracts =
    (polyPos?.contracts ?? 0) > 0 || (kalshiPos?.contracts ?? 0) > 0;
  const setPairUnwinderEnabled = useSetPairUnwinderEnabled();
  const [confirmOff, setConfirmOff] = useState(false);

  const onTogglePairEnabled = (next: boolean) => {
    if (!group._id || setPairUnwinderEnabled.isLoading) return;
    if (!next && hasOpenContracts) {
      setConfirmOff(true);
      return;
    }
    setPairUnwinderEnabled.mutate({ pairId: group._id, enabled: next });
  };
  // Keep inputs in sync with rule changes only when user is not mid-edit.
  useEffect(() => {
    if (!rule) return;
    const dirty =
      threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;
    if (dirty) return;
    if (rule.thresholdSum != null) {
      const next = String(rule.thresholdSum);
      if (next !== savedThreshold) {
        setThreshold(next);
        setSavedThreshold(next);
      }
    }
    const ruleSizeNow =
      rule.kalshiConfig?.contractsToSell ?? rule.polymarketConfig?.contractsToSell;
    if (ruleSizeNow != null) {
      const next = String(ruleSizeNow);
      if (next !== savedSize) {
        setSize(next);
        setSavedSize(next);
      }
    }
    const nextMax = String(rule.maxVolumeContracts);
    if (nextMax !== savedMaxVol) {
      setMaxVol(nextMax);
      setSavedMaxVol(nextMax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.thresholdSum, rule?.maxVolumeContracts, rule?.kalshiConfig?.contractsToSell, rule?.polymarketConfig?.contractsToSell]);

  const isDirty =
    threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;

  // Parameter edits are allowed while a platform is live. The backend
  // monitoring engine reacts to thresholdSum / contractsToSell / maxVolumeContracts
  // changes via `ruleParamsChanged` and re-evaluates immediately
  // (`MonitoringEngine.reloadRules` → `evaluateRule`), so a resting maker
  // gets repriced/resized in place rather than silently drifting.
  const paramsLocked = false;

  const handleSave = useCallback(async () => {
    if (!isDirty || !rule) return;
    const parsedThreshold = Number.parseFloat(threshold);
    const parsedSize = Number.parseInt(size, 10);
    const parsedMaxVol = Number.parseFloat(maxVol);
    const dto: UpdateUnwindingRuleDto = {};
    if (
      Number.isFinite(parsedThreshold) &&
      parsedThreshold >= 0 &&
      parsedThreshold <= 2 &&
      threshold !== savedThreshold
    ) {
      dto.thresholdSum = parsedThreshold;
    }
    if (Number.isFinite(parsedMaxVol) && parsedMaxVol > 0 && maxVol !== savedMaxVol) {
      dto.maxVolumeContracts = parsedMaxVol;
    }
    if (Number.isFinite(parsedSize) && parsedSize > 0 && size !== savedSize) {
      // contractsToSell lives on each platform config; mirror to both.
      if (rule.kalshiConfig) {
        dto.kalshiConfig = {
          side: rule.kalshiConfig.side,
          ticker: rule.kalshiConfig.ticker,
          contractsToSell: parsedSize
        };
      }
      if (rule.polymarketConfig) {
        dto.polymarketConfig = {
          side: rule.polymarketConfig.side,
          tokenId: rule.polymarketConfig.tokenId,
          conditionId: rule.polymarketConfig.conditionId,
          contractsToSell: parsedSize
        };
      }
    }
    setSaving(true);
    const ok = await onUpdateRule(rule, dto);
    if (ok) {
      setSavedThreshold(threshold);
      setSavedSize(size);
      setSavedMaxVol(maxVol);
    }
    setSaving(false);
  }, [
    isDirty,
    threshold,
    size,
    maxVol,
    savedThreshold,
    savedSize,
    savedMaxVol,
    rule,
    onUpdateRule
  ]);

  const doToggle = useCallback(
    async (platform: 'kalshi' | 'polymarket', enabled: boolean) => {
      const setLoading = platform === 'kalshi' ? setTogglingKalshi : setTogglingPoly;
      setLoading(true);
      try {
        if (rule) {
          // Mutation handles optimistic cache patch in onMutate; the
          // spinner unsticks the moment the HTTP round-trip lands —
          // no fixed 2s timer.
          await onTogglePlatform(rule, platform, enabled);
        } else if (enabled) {
          const parsedThr = Number.parseFloat(threshold);
          const parsedSize = Number.parseInt(size, 10);
          const parsedMaxV = Number.parseFloat(maxVol);
          await onCreateRule({
            group,
            platform,
            thresholdSum:
              Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr <= 2
                ? parsedThr
                : DEFAULT_THRESHOLD,
            size:
              Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : DEFAULT_SIZE,
            maxVolumeContracts:
              Number.isFinite(parsedMaxV) && parsedMaxV > 0 ? parsedMaxV : DEFAULT_MAX_VOL
          });
        }
      } catch {
        /* mutation onError rollback handles cache state */
      } finally {
        setLoading(false);
      }
    },
    [rule, threshold, size, maxVol, group, onCreateRule, onTogglePlatform]
  );

  const handleToggle = useCallback(
    (platform: 'kalshi' | 'polymarket', enabled: boolean) => {
      doToggle(platform, enabled);
    },
    [doToggle]
  );

  // Live market data — shown ONLY when the pair is actually monitored.
  // When it isn't (kill switch off, or below the subscription constraint)
  // every market-data cell AND the Live Sums / Simulation columns derived
  // from these four prices fall back to "—": the unwinder is bringing no
  // data for this pair, so rendering the last (now stale) prices would lie.
  const pBid = isMonitored ? liveState?.polyBid?.price ?? null : null;
  const pAsk = isMonitored ? liveState?.polyAsk?.price ?? null : null;
  const kBid = isMonitored ? liveState?.kalshiBid?.price ?? null : null;
  const kAsk = isMonitored ? liveState?.kalshiAsk?.price ?? null : null;
  const pBidSize = isMonitored ? liveState?.polyBid?.size : undefined;
  const pAskSize = isMonitored ? liveState?.polyAsk?.size : undefined;
  const kBidSize = isMonitored ? liveState?.kalshiBid?.size : undefined;
  const kAskSize = isMonitored ? liveState?.kalshiAsk?.size : undefined;

  // SELL-direction sim cells come straight from the Rust unwinder engine.
  // The Rust task already applies the correct Polymarket category fees +
  // Kalshi P(1-P) taker fee, so the FE never computes them. When the
  // pair isn't monitored OR the Rust cache hasn't seeded yet,
  // `liveState.engineSim` / `liveState.simCells` are `null` and every
  // derived cell renders `—`.
  const liveStateForSim = isMonitored ? liveState : undefined;
  const polyPosSide = polyPos?.side ?? 'yes';
  const kalshiPosSide = kalshiPos?.side ?? 'yes';
  const pathSim = pickEnginePathSim(liveStateForSim, polyPosSide);
  // Live strategy sums (fee-adjusted on the taker leg):
  //  - MT-P: maker rests at P.Ask, taker hits K.Bid (fee'd)
  //  - MT-K: maker rests at K.Ask, taker hits P.Bid (fee'd)
  //  - TT:   both legs taken at bid (both fee'd)
  const mtPoly = pathSim?.sumPolyMk ?? null;
  const mtKalshi = pathSim?.sumKalshiMk ?? null;
  const tt = pathSim?.sumTT ?? null;

  const thresholdNum = Number.parseFloat(threshold);
  const thresholdValid = Number.isFinite(thresholdNum) && thresholdNum >= 0;
  // Δ compares the headline (taker-taker, fee-adjusted) sum against threshold.
  const delta = tt != null && thresholdValid ? tt - thresholdNum : null;
  const meetsThreshold = delta != null && delta >= 0;

  // Taker TNA: annualized return if both legs are taken right now. Uses the
  // fee-adjusted TT and liveState.daysToExpiry (group.daysToExpiry is the
  // REST-seed fallback for the first paint). Same continuous-compounding
  // formula the backend uses for the rule's `achievedTna`.
  const takerTna = takerTnaFromSum(
    tt,
    liveState?.daysToExpiry ?? group.daysToExpiry ?? null
  );

  // Maker-sim cells — orange = net counter taker (what the other leg
  // pays/receives), blue = maker rest price = threshold − taker. Backend
  // applies the same `threshold − netTakerBid` math; we read the values
  // from the Rust simCells payload so the displayed cells are bit-for-bit
  // identical to what the engine sees.
  const polyMakerCell = pickPolyMakerCell(liveStateForSim, polyPosSide);
  const kalshiMakerCell = pickKalshiMakerCell(liveStateForSim, kalshiPosSide);
  const simPolyMakerTaker = polyMakerCell?.taker ?? null;
  const simPolyMakerMaker = polyMakerCell?.maker ?? null;
  const simKalshiMakerTaker = kalshiMakerCell?.taker ?? null;
  const simKalshiMakerMaker = kalshiMakerCell?.maker ?? null;

  const inSx = {
    '& .MuiInputBase-input': {
      fontSize: '0.62rem',
      py: 0.15,
      px: 0.2,
      textAlign: 'right',
      width: 28
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.08)'
    },
    minWidth: 0
  } as const;

  const tooltipTitle = (
    <Box>
      <Typography sx={{ fontSize: '0.7rem' }}>
        {group.polymarketTitle || group.pairName}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
        {group.kalshiTitle || kalshiPos?.marketId || group.pairName}
      </Typography>
    </Box>
  );

  return (
    <>
    <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
      {/* Bulk select */}
      <TableCell
        sx={{ ...cSx, px: 0, verticalAlign: 'middle' }}
        align="center"
        padding="none"
        onClick={e => e.stopPropagation()}
      >
        <Checkbox
          size="small"
          checked={selected}
          disabled={!group._id}
          onChange={() => group._id && onToggleSelect(group._id)}
          sx={{ p: 0, color: 'grey.600', '& .MuiSvgIcon-root': { fontSize: 14 } }}
        />
      </TableCell>
      {/* Market */}
      <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.2 }}>
          {/* Dev-logs trigger. Mirrors the TNA tab: enabled when ANY rule
              exists for the pair (regardless of type/status), so a pair
              with a TNA rule still has clickable logs from this tab. */}
          <Tooltip title={debugRuleId ? 'Open dev logs' : 'No rule yet'} placement="top">
            <span>
              <IconButton
                size="small"
                disabled={!debugRuleId}
                onClick={(e) => {
                  e.stopPropagation();
                  if (debugRuleId) onOpenDebug(debugRuleId, group.pairName ?? '');
                }}
                sx={{ p: 0, color: debugRuleId ? 'grey.500' : 'grey.700' }}
              >
                <BugReportIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              isMonitored
                ? 'Monitored — unwinder is streaming market data. Click to turn off.'
                : killSwitchOff
                  ? 'OFF — turned off; the unwinder is bringing no market data. Click to turn on.'
                  : 'Not monitored — no live market data (pair is below the unwinder contract minimum, or only one leg is open).'
            }
            placement="top"
          >
            <span>
              <Switch
                size="small"
                checked={isMonitored}
                disabled={setPairUnwinderEnabled.isLoading}
                onChange={(e) => {
                  e.stopPropagation();
                  onTogglePairEnabled(e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                disableRipple
                sx={{
                  m: 0,
                  p: 0,
                  width: 18,
                  height: 10,
                  display: 'inline-flex',
                  '& .MuiSwitch-switchBase': {
                    p: '1px',
                    '&.Mui-checked': {
                      transform: 'translateX(8px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#7c3aed',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    width: 8,
                    height: 8,
                    boxShadow: 'none',
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 5,
                    backgroundColor: 'rgba(255,255,255,0.18)',
                    opacity: 1,
                  },
                }}
              />
            </span>
          </Tooltip>
        <Tooltip title={tooltipTitle} placement="top-start">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <a
                href={polyPos?.marketUrl ?? liveState?.polymarketMarketUrl ?? undefined}
                target="_blank"
                rel="noopener"
                onClick={e => e.stopPropagation()}
              >
                <img
                  src="/assets/exchanges/png/100x100/polymarket.png"
                  alt="P"
                  style={{ width: 13, height: 13, borderRadius: '50%' }}
                />
              </a>
              <a
                href={polyPos?.marketUrl ?? liveState?.polymarketMarketUrl ?? undefined}
                target="_blank"
                rel="noopener"
                onClick={e => e.stopPropagation()}
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  color: 'inherit',
                  textDecoration: 'none'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {group.polymarketTitle || group.pairName}
                </Typography>
              </a>
              {polyPos && (
                <Chip
                  label={polyPos.side.toUpperCase()}
                  size="small"
                  color={polyPos.side === 'yes' ? 'success' : 'error'}
                  variant="outlined"
                  sx={{
                    fontSize: '0.42rem',
                    height: 12,
                    '& .MuiChip-label': { px: 0.25 },
                    opacity: 0.7
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <a
                href={kalshiPos?.marketUrl ?? liveState?.kalshiMarketUrl ?? undefined}
                target="_blank"
                rel="noopener"
                onClick={e => e.stopPropagation()}
              >
                <img
                  src="/assets/exchanges/png/100x100/kalshi.png"
                  alt="K"
                  style={{ width: 13, height: 13, borderRadius: '50%' }}
                />
              </a>
              <a
                href={kalshiPos?.marketUrl ?? liveState?.kalshiMarketUrl ?? undefined}
                target="_blank"
                rel="noopener"
                onClick={e => e.stopPropagation()}
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  color: 'inherit',
                  textDecoration: 'none'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    color: 'grey.400',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {group.kalshiTitle || kalshiPos?.marketId || group.pairName}
                </Typography>
              </a>
              {kalshiPos && (
                <Chip
                  label={kalshiPos.side.toUpperCase()}
                  size="small"
                  color={kalshiPos.side === 'yes' ? 'success' : 'error'}
                  variant="outlined"
                  sx={{
                    fontSize: '0.42rem',
                    height: 12,
                    '& .MuiChip-label': { px: 0.25 },
                    opacity: 0.7
                  }}
                />
              )}
            </Box>
          </Box>
        </Tooltip>
        </Box>
      </TableCell>

      {/* Pos */}
      <TableCell sx={{ ...cSx, verticalAlign: 'top' }} align="right">
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4 }}>
          {fmtContracts(polyPos?.contracts)}
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4 }}>
          {fmtContracts(kalshiPos?.contracts)}
        </Typography>
      </TableCell>

      {/* Market Data — match TNA tab: Qty, P.Bid, P.Ask, Qty, Qty, K.Bid, K.Ask, Qty */}
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(pBidSize)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(pBid)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(pAsk)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(pAskSize)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(kBidSize)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(kBid)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(kAsk)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(kAskSize)}</Typography>
      </TableCell>

      {/* Parameters: Threshold | Size | MaxV (last cell carries the save tick).
          Inputs are locked while the rule is live (any platform enabled) —
          pause both platforms to edit. */}
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Tooltip
          title={paramsLocked ? 'Pause both platforms to edit parameters' : ''}
          placement="top"
          disableHoverListener={!paramsLocked}
        >
          <span>
            <TextField
              variant="outlined"
              size="small"
              value={threshold}
              placeholder="1.00"
              onChange={e => setThreshold(e.target.value)}
              onBlur={handleSave}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSave();
              }}
              disabled={paramsLocked}
              sx={inSx}
              inputProps={{ step: 0.01, min: 0, max: 2 }}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Tooltip
          title={paramsLocked ? 'Pause both platforms to edit parameters' : ''}
          placement="top"
          disableHoverListener={!paramsLocked}
        >
          <span>
            <TextField
              variant="outlined"
              size="small"
              value={size}
              placeholder="50"
              onChange={e => setSize(e.target.value)}
              onBlur={handleSave}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSave();
              }}
              disabled={paramsLocked}
              sx={inSx}
              inputProps={{ step: 1, min: 1 }}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Stack direction="row" alignItems="center" spacing={0} justifyContent="flex-end">
          <Tooltip
            title={paramsLocked ? 'Pause both platforms to edit parameters' : ''}
            placement="top"
            disableHoverListener={!paramsLocked}
          >
            <span>
              <TextField
                variant="outlined"
                size="small"
                value={maxVol}
                placeholder="500"
                onChange={e => setMaxVol(e.target.value)}
                onBlur={handleSave}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSave();
                }}
                disabled={paramsLocked}
                sx={inSx}
                inputProps={{ step: 100, min: 0 }}
              />
            </span>
          </Tooltip>
          {saving ? (
            <CircularProgress size={12} sx={{ ml: 0.2 }} />
          ) : isDirty && rule ? (
            <IconButton
              size="small"
              onClick={handleSave}
              sx={{ p: 0.1, ml: 0.2, color: 'success.main' }}
            >
              <CheckIcon sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          ) : null}
        </Stack>
      </TableCell>

      {/* Live Sums */}
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.7rem', color: 'grey.300' }}>{fmtPrice(mtPoly)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.7rem', color: 'grey.300' }}>{fmtPrice(mtKalshi)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 700,
            color: meetsThreshold ? '#4caf50' : '#42a5f5'
          }}
        >
          {fmtPrice(tt)}
        </Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography
          sx={{
            fontSize: '0.7rem',
            color: delta == null ? 'grey.500' : delta >= 0 ? '#4caf50' : '#f44336'
          }}
        >
          {delta == null ? '—' : (delta >= 0 ? '+' : '') + delta.toFixed(3)}
        </Typography>
      </TableCell>

      {/* Taker TNA — annualized return if BOTH legs execute as taker right
          now, computed from the TT sum (net of taker fees on both sides) and
          liveState.daysToExpiry using continuous compounding. Mirrors the
          backend RateCalculator.calculatePositionTna formula. */}
      <TableCell sx={cSx} align="right">
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color:
              takerTna == null
                ? 'grey.500'
                : takerTna >= 0
                  ? '#4caf50'
                  : '#f44336'
          }}
        >
          {fmtPct(takerTna)}
        </Typography>
      </TableCell>

      {/* Simulation: K.Bid (orange), P.Ask (blue), P.Bid (orange), K.Ask (blue) */}
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.7rem', color: '#ff9800' }}>
          {fmtPrice(simPolyMakerTaker)}
        </Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.7rem', color: '#42a5f5', fontWeight: 600 }}>
          {fmtPrice(simPolyMakerMaker)}
        </Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.7rem', color: '#ff9800' }}>
          {fmtPrice(simKalshiMakerTaker)}
        </Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.7rem', color: '#42a5f5', fontWeight: 600 }}>
          {fmtPrice(simKalshiMakerMaker)}
        </Typography>
      </TableCell>

      {/* Maker toggles */}
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingPoly ? (
          <CircularProgress size={14} />
        ) : (
          <Switch
            size="small"
            checked={rule ? rule.polymarketEnabled !== false : false}
            onChange={(_, v) => handleToggle('polymarket', v)}
          />
        )}
      </TableCell>
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingKalshi ? (
          <CircularProgress size={14} />
        ) : (
          <Switch
            size="small"
            checked={rule ? rule.kalshiEnabled !== false : false}
            onChange={(_, v) => handleToggle('kalshi', v)}
          />
        )}
      </TableCell>
    </TableRow>
    <Dialog
      open={confirmOff}
      onClose={() => setConfirmOff(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Turn off unwinder for this pair?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {group.pairName} still has open contracts (Polymarket:{' '}
          {polyPos?.contracts ?? 0}, Kalshi: {kalshiPos?.contracts ?? 0}).
          Turning the unwinder off pauses the rule and stops streaming
          market data; the position stays open and must be managed
          manually until the pair is turned back on.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOff(false)} size="small">
          Cancel
        </Button>
        <Button
          color="warning"
          variant="contained"
          size="small"
          onClick={() => {
            if (!group._id) return;
            setPairUnwinderEnabled.mutate(
              { pairId: group._id, enabled: false },
              { onSettled: () => setConfirmOff(false) },
            );
          }}
        >
          Turn off
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}, (prev, next) => (
  prev.group === next.group
  && prev.polyPos === next.polyPos
  && prev.kalshiPos === next.kalshiPos
  && prev.rule === next.rule
  && prev.liveState === next.liveState
  && prev.selected === next.selected
  && prev.onToggleSelect === next.onToggleSelect
  && prev.onCreateRule === next.onCreateRule
  && prev.onTogglePlatform === next.onTogglePlatform
  && prev.onUpdateRule === next.onUpdateRule
));
